import React from 'react';
import _ from 'lodash';
import PropTypes from 'prop-types';
import moment from 'moment';

import SimpleSubmitSection from '../../../common/components/containers/EntityForm/internal/SimpleSubmitSection';
import TextField from '../../../common/components/containers/EntityForm/fields/TextField';
import EntityForm from '../../../common/components/containers/EntityForm';
import EntityFormFieldSet from '../../../common/components/containers/EntityForm/EntityFormFieldSet';
import PasswordValidationField from '../../../common/components/containers/EntityForm/fields/PasswordValidationField';
import PasswordExpirationInfo from './PasswordExpirationInfo';

import withCurrentUserPasswordInfo from '../../../common/data/withCurrentUserPasswordInfo';
import withTranslations from '../../../common/components/utils/Translations/withTranslations';
import propTypes from '../../../common/propTypes';

import FormPasswordValidation, {
  applyValidators,
} from '../../../common/components/controls/FormPasswordValidation';
import { getPasswordValidatorsByPasswordStrength } from '../../../model/passwordValidator';

const DAY_IN_YEAR = 365;
@withTranslations
@withCurrentUserPasswordInfo({ inline: false })
export default class UpdatePasswordForm extends React.PureComponent {
  static propTypes = {
    currentUserPasswordInfo: propTypes.personPasswordInfo.isRequired,
    updateMessageText: PropTypes.string.isRequired,
    ...propTypes.translatePropTypes,
  };

  constructor(props) {
    super(props);
    this.state = {
      isValid: false,
    };
  }

  validate = ({ newPassword, passwordConfirmation, currentPassword }) => {
    const {
      currentUserPasswordInfo: { passwordStrength },
      t,
    } = this.props;

    const validator = getPasswordValidatorsByPasswordStrength(
      passwordStrength,
      t,
    );
    const { totalValidationResults } = applyValidators(
      validator,
      newPassword,
      passwordConfirmation,
    );

    const isValid =
      _.every(totalValidationResults, 'isValid') && !_.isEmpty(currentPassword);

    this.setState({ isValid });
  };

  renderSubmitSection = ({ resetForm }) => {
    const { t } = this.props;
    const { isValid } = this.state;

    return (
      <SimpleSubmitSection
        isEditable
        isRoundedLinkButton
        withoutErrors
        goBackLabel={t('Cancel')}
        isDisable={!isValid}
        submitLabel={t('Save')}
        onGoBack={resetForm}
      />
    );
  };

  checkPasswordExpiry = passwordExpiresAt => {
    if (!passwordExpiresAt) {
      return false;
    }

    const currentDate = moment();
    const expiryDate = moment(passwordExpiresAt);

    return expiryDate.diff(currentDate, 'days') <= DAY_IN_YEAR;
  };

  render() {
    const {
      t,
      currentUserPasswordInfo: { passwordExpiresAt, passwordStrength },
      onSubmit,
      columns,
      entity,
      entityName,
      updateMessageText,
    } = this.props;

    const isPasswordExpiryValid = this.checkPasswordExpiry(passwordExpiresAt);

    return (
      <EntityForm
        columns={columns}
        entity={entity}
        entityName={entityName}
        hasUpdateMessage={!!updateMessageText}
        submitSectionRenderer={this.renderSubmitSection}
        updateMessageText={updateMessageText}
        validate={this.validate}
        onSubmit={onSubmit}
      >
        {isPasswordExpiryValid && (
          <EntityFormFieldSet>
            <div className="col-lg-6 col-md-6 col-sm-12">
              <PasswordExpirationInfo expiresAt={passwordExpiresAt} />
            </div>
          </EntityFormFieldSet>
        )}
        <EntityFormFieldSet>
          <div className="col-lg-3 col-md-6 col-sm-12">
            <div className="row">
              <TextField
                isEditable
                required
                label={t('Current Password')}
                name="currentPassword"
                type="password"
              />
            </div>
          </div>
        </EntityFormFieldSet>
        <EntityFormFieldSet>
          <div className="col-lg-3 col-md-6 col-sm-12">
            <EntityFormFieldSet>
              <TextField
                isEditable
                required
                label={t('New Password')}
                name="newPassword"
                type="password"
              />
            </EntityFormFieldSet>
            <EntityFormFieldSet>
              <TextField
                isEditable
                required
                label={t('Confirm Password')}
                name="passwordConfirmation"
                type="password"
              />
            </EntityFormFieldSet>
          </div>
          <div className="col-lg-4 col-md-6 col-sm-12">
            <EntityFormFieldSet>
              <PasswordValidationField
                className="list-unstyled m-t-30"
                name="passwordValidation"
                passwordConfirmationFieldName="passwordConfirmation"
                passwordFieldName="newPassword"
                passwordStrength={passwordStrength}
              />
            </EntityFormFieldSet>
          </div>
        </EntityFormFieldSet>
      </EntityForm>
    );
  }
}
